/* ============================================================================
   TRAER METAR

   Se piden al PROPIO servidor, en `/metar/<OACI>`, y él los trae de afuera.
   Mismo motivo que los mosaicos del mapa: en el iPad, con el certificado
   propio, el navegador bloquea las peticiones a otros dominios, y el METAR
   quedaría vacío justo en el equipo que se lleva a la cabina.

   Lo que este módulo NO hace es decidir nada. Trae el texto crudo; leerlo y
   clasificarlo es de `domain/metar.js`, que es puro y se prueba sin red.
   ========================================================================== */

/** Cuánto vale un informe ya traído. Los METAR se emiten cada media hora. */
export const VIGENCIA_MS = 5 * 60 * 1000;

const cache = new Map();

/** Códigos OACI: cuatro letras. Se comprueba antes de armar ninguna URL. */
export function oaciValido(codigo) {
  return typeof codigo === "string" && /^[A-Za-z]{4}$/.test(codigo.trim());
}

/**
 * Trae el METAR de un aeródromo.
 *
 * Devuelve siempre un objeto con `crudo` (el texto o `null`) y `motivo`
 * cuando no se pudo. Nunca lanza: que falte el METAR no puede tumbar la
 * pantalla de planificación, y «no hay informe» es una respuesta legítima —
 * muchos aeródromos chicos no publican.
 */
export async function traerMetar(oaci, { traer = fetch, ahora = Date.now() } = {}) {
  if (!oaciValido(oaci)) {
    return { oaci: null, crudo: null, motivo: "Código OACI inválido." };
  }
  const clave = oaci.trim().toUpperCase();

  const guardado = cache.get(clave);
  if (guardado && ahora - guardado.cuando < VIGENCIA_MS) {
    return { oaci: clave, crudo: guardado.crudo, motivo: null, origen: "cache" };
  }

  let r;
  try {
    r = await traer(`/metar/${clave}`, { cache: "no-store" });
  } catch {
    return { oaci: clave, crudo: null, motivo: "No se pudo contactar al servidor." };
  }

  if (r.status === 404) {
    /* NO es un error: muchos aeródromos simplemente no publican METAR. Hay que
       poder distinguirlo de una caída, porque la respuesta del piloto es
       distinta. */
    return { oaci: clave, crudo: null, motivo: "Este aeródromo no publica METAR.", sinServicio: true };
  }
  if (r.status === 504) return { oaci: clave, crudo: null, motivo: "El servidor no tiene internet para consultar el METAR." };
  if (r.status === 502) return { oaci: clave, crudo: null, motivo: "El servicio de METAR no está respondiendo." };
  if (!r.ok) return { oaci: clave, crudo: null, motivo: `El servidor contestó ${r.status}.` };

  let datos;
  try {
    datos = await r.json();
  } catch {
    return { oaci: clave, crudo: null, motivo: "La respuesta del servidor no se pudo leer." };
  }

  const crudo = typeof datos?.crudo === "string" ? datos.crudo.trim() : "";
  if (!crudo) return { oaci: clave, crudo: null, motivo: "El servidor contestó sin informe." };

  cache.set(clave, { crudo, cuando: ahora });
  return { oaci: clave, crudo, motivo: null, origen: datos.origen || "red" };
}

/**
 * Trae varios a la vez.
 *
 * En paralelo y sin que uno arrastre a los otros: si el METAR de destino
 * falla, el de salida tiene que llegar igual. Un `Promise.all` que rechaza
 * dejaría los dos vacíos.
 */
export async function traerVarios(codigos = [], opciones = {}) {
  const unicos = [...new Set(codigos.filter(oaciValido).map((c) => c.trim().toUpperCase()))];
  const resultados = await Promise.all(unicos.map((c) => traerMetar(c, opciones)));
  return Object.fromEntries(resultados.map((r) => [r.oaci, r]));
}

/** Vacía la caché. Para las pruebas y para el botón de actualizar. */
export function olvidar() {
  cache.clear();
}
